/**
 * branchOrder — pure helpers for sequence branch evaluation order.
 *
 * WHY THIS EXISTS
 * ---------------
 * `message_sequence_steps.branch_order` decides which branch a contact takes,
 * but until now no UI control set it. The worker
 * (supabase/functions/process-sequence-steps/index.ts, runBranchPhase) does:
 *
 *   1. Load active children of the current step, ORDER BY branch_order ASC.
 *   2. Walk them in that order. Any child with branch_condition = 'default' is
 *      NOT evaluated in the loop — it is set aside as `defaultChild`.
 *   3. The FIRST conditional child that matches wins and the loop breaks.
 *   4. chosenChild = matchedChild || defaultChild.
 *   5. No match and no default => the enrollment is COMPLETED silently.
 *
 * Two consequences the operator has to be able to see:
 *
 *   A. Two conditional siblings sharing a branch_order is a real ambiguity.
 *      Postgres has no defined order between them, so a contact matching both
 *      can get either branch, and it can differ between runs. This shipped:
 *      "Defend Member Journey" had both first-split branches at branch_order 0
 *      from May until 2026-09-09.
 *   B. A split with no 'default' child drops every contact that matches none
 *      of the conditions — they complete and receive nothing further.
 *
 * These helpers are deliberately dependency-free so they can be unit tested
 * and reused by both the step editor and the tree view.
 */

/** The subset of a sequence step this module needs. Structurally compatible
 *  with SequenceStep, so a SequenceStep[] can be passed directly. */
export interface BranchSibling {
  id: string;
  name?: string | null;
  step_type?: string;
  branch_condition: string;
  /** Nullable in the DB (integer NULL, default 0). PostgREST orders ASC with
   *  NULLS LAST, so an unset order evaluates last — mirrored below. */
  branch_order?: number | null;
  created_at?: string | null;
  /** Carried for the caller's convenience only. This module does NOT filter on
   *  it — the worker evaluates active children only, so callers should pass a
   *  list they have already filtered when they want runtime-accurate warnings. */
  is_active?: boolean;
}

export type BranchWarningCode = "tied_branch_order" | "no_default_branch";

export interface BranchWarning {
  code: BranchWarningCode;
  /** One-line headline, operator language, no jargon. */
  message: string;
  /** What actually happens to a contact because of it. */
  consequence: string;
  /** Ids of the steps involved, so a caller can highlight them. */
  stepIds: string[];
}

export const DEFAULT_BRANCH_CONDITION = "default";

export function isFallbackBranch(step: BranchSibling): boolean {
  return step.branch_condition === DEFAULT_BRANCH_CONDITION;
}

/**
 * Split a sibling group into the branches that are evaluated in order and the
 * fallback ('default') branches that are not.
 *
 * More than one fallback is possible in the data; the worker keeps the LAST
 * one it sees while walking in branch_order, so we mirror that ordering here.
 */
export function splitBranches(siblings: BranchSibling[]): {
  conditional: BranchSibling[];
  fallback: BranchSibling[];
} {
  const conditional: BranchSibling[] = [];
  const fallback: BranchSibling[] = [];
  for (const s of siblings) {
    (isFallbackBranch(s) ? fallback : conditional).push(s);
  }
  return { conditional, fallback };
}

/**
 * Order conditional branches the way the worker evaluates them:
 * branch_order ASC with NULL last (Postgres/PostgREST ASC default), then a
 * stable tiebreak on created_at and id.
 *
 * The tiebreak is DISPLAY ONLY. It does not make a tied group deterministic at
 * runtime — that is exactly what the tie warning is for.
 */
export function orderConditionalBranches(
  siblings: BranchSibling[],
): BranchSibling[] {
  return [...splitBranches(siblings).conditional].sort((a, b) => {
    const ao = a.branch_order;
    const bo = b.branch_order;
    const aNull = ao === null || ao === undefined;
    const bNull = bo === null || bo === undefined;
    if (aNull !== bNull) {
      return aNull ? 1 : -1;
    }
    if (!aNull && !bNull && ao !== bo) {
      return (ao as number) - (bo as number);
    }
    const ac = a.created_at ?? "";
    const bc = b.created_at ?? "";
    if (ac !== bc) {
      return ac < bc ? -1 : 1;
    }
    if (a.id === b.id) {
      return 0;
    }
    return a.id < b.id ? -1 : 1;
  });
}

/**
 * Groups of conditional siblings that share a branch_order (an unset order is
 * its own group, since NULLs are equally unordered among themselves).
 * Only groups of 2+ are returned.
 */
export function findTiedBranchOrders(
  siblings: BranchSibling[],
): Array<{ branchOrder: number | null; steps: BranchSibling[] }> {
  const buckets = new Map<string, BranchSibling[]>();
  for (const s of splitBranches(siblings).conditional) {
    const key =
      s.branch_order === null || s.branch_order === undefined
        ? "null"
        : String(s.branch_order);
    const list = buckets.get(key);
    if (list) {
      list.push(s);
    } else {
      buckets.set(key, [s]);
    }
  }
  const out: Array<{ branchOrder: number | null; steps: BranchSibling[] }> = [];
  for (const [key, steps] of buckets) {
    if (steps.length > 1) {
      out.push({
        branchOrder: key === "null" ? null : Number(key),
        steps,
      });
    }
  }
  return out.sort((a, b) => {
    if (a.branchOrder === null) {
      return 1;
    }
    if (b.branchOrder === null) {
      return -1;
    }
    return a.branchOrder - b.branchOrder;
  });
}

/**
 * True when this split has more than one branch child and none of them is the
 * fallback ('default') branch.
 */
export function isMissingFallbackBranch(siblings: BranchSibling[]): boolean {
  return siblings.length > 1 && splitBranches(siblings).fallback.length === 0;
}

function labelFor(step: BranchSibling): string {
  return step.name?.trim() || step.step_type || "Untitled step";
}

/**
 * The two validations, as WARNINGS. Nothing here blocks a save — a sequence
 * that is mid-edit is allowed to be temporarily wrong.
 */
export function validateBranchGroup(
  siblings: BranchSibling[],
): BranchWarning[] {
  const warnings: BranchWarning[] = [];

  for (const tie of findTiedBranchOrders(siblings)) {
    const names = tie.steps.map(labelFor).join(", ");
    const position =
      tie.branchOrder === null
        ? "no evaluation position set"
        : `evaluation position ${tie.branchOrder + 1}`;
    warnings.push({
      code: "tied_branch_order",
      message: `${tie.steps.length} branches share the same place in the queue (${position}): ${names}.`,
      consequence:
        "A contact who matches more than one of them could take either branch, and it can come out differently from one run to the next. Give each branch its own position so the outcome is predictable.",
      stepIds: tie.steps.map((s) => s.id),
    });
  }

  if (isMissingFallbackBranch(siblings)) {
    warnings.push({
      code: "no_default_branch",
      message: `None of these ${siblings.length} branches is the fallback ("Default").`,
      consequence:
        "A contact who matches none of the conditions stops here: the sequence finishes for them and they receive nothing further. Add a branch with condition “Default” to catch them.",
      stepIds: siblings.map((s) => s.id),
    });
  }

  return warnings;
}

/**
 * branch_order to give a NEW conditional branch so it is evaluated last and
 * cannot tie with an existing sibling.
 */
export function nextBranchOrder(siblings: BranchSibling[]): number {
  const orders = splitBranches(siblings)
    .conditional.map((s) => s.branch_order)
    .filter((o): o is number => typeof o === "number");
  if (orders.length === 0) {
    return 0;
  }
  return Math.max(...orders) + 1;
}

/** Move the item at `index` one place up (-1) or down (+1). Returns a new
 *  array; out-of-range moves return the input order unchanged. */
export function moveInOrder<T>(items: T[], index: number, delta: -1 | 1): T[] {
  const target = index + delta;
  if (
    index < 0 ||
    index >= items.length ||
    target < 0 ||
    target >= items.length
  ) {
    return items;
  }
  const next = [...items];
  const [moved] = next.splice(index, 1);
  next.splice(target, 0, moved);
  return next;
}

/**
 * Which siblings actually need a write to land the given order as 0..n-1.
 * Anything already at its target index is left alone — a reorder must not
 * touch rows it does not need to change.
 */
export function branchOrderWrites(
  orderedIds: string[],
  storedOrders: Map<string, number | null | undefined>,
): Array<{ id: string; branch_order: number }> {
  const writes: Array<{ id: string; branch_order: number }> = [];
  orderedIds.forEach((id, index) => {
    if (storedOrders.get(id) !== index) {
      writes.push({ id, branch_order: index });
    }
  });
  return writes;
}
